/*
 * File: SalesOrderTypeInput.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 18 May 2021 5:00:00 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { Select, Space, Spin, Typography } from 'antd';
import { CreateSalesOrderType } from 'common/enums/CreateSalesOrderType';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReduxState } from 'redux/rootReducer';
import { useGetSalesOrderTypesQuery } from 'redux/services/useGrannyFannyNesselrodeService';
import { BlockLoader } from '../BlockLoader';

const styles: InlineStylesModel = {
  formItem: {
    marginBottom: 10,
    width: '100%'
  },
  formItemCol: {
    marginRight: '16px',
    maxHeight: '42px'
  },
  titleColor: {
    color: '#2F5596'
  }
};

const { Title } = Typography;

export const SalesOrderTypeInput = (): JSX.Element => {
  const {
    values: { salesOrderType },
    setFieldValue
  } = useFormikContext<SalesOrderPayloadModel>();
  const { selectedCompanyDataAreaId: dataAreaId } = useSelector((state: ReduxState) => state.salesOrders);

  const [inputValue, _setInputValue] = useState<string | undefined>(undefined);
  const [offset, _setOffset] = useState(0);
  const { pathname } = useLocation();
  const isPosted = pathname.includes('/posted');

  /* ****************** Hook API ****************** */
  const { data: salesOrderTypesResponse, isFetching, isLoading } = useGetSalesOrderTypesQuery({ dataAreaId, offset });

  /* ****************** JSX / Variables ****************** */
  const salesOrderTypesOptions = useMemo(
    () => salesOrderTypesResponse?.data?.map((type) => ({ label: `${type.salesOrderType} - ${type.salesOrderTypeDescription}`, value: type.salesOrderType })),
    [salesOrderTypesResponse]
  );

  /* ****************** Functions ****************** */
  // Handle delay of searching data
  // const handleSearch = useDebouncedCallback((value: string | undefined) => {
  //   _setOffset(0);
  //   _setInputValue(value);
  // }, 600);

  const handleClear = (): void => {
    _setInputValue(undefined);
    _setOffset(0);
    setFieldValue('salesOrderType' as keyof SalesOrderPayloadModel, null);
  };

  // const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
  //   const target = event.currentTarget;

  //   if (!isLoading && !isFetching && target.scrollTop + target.offsetHeight === target.scrollHeight && salesOrderTypesResponse && salesOrderTypesResponse?.totalCount - offset > 25) {
  //     _setOffset((prevState) => prevState + 25);
  //   }
  // };

  const handleSelect = (value: string): void => {
    setFieldValue('salesOrderType' as keyof SalesOrderPayloadModel, value);
    _setInputValue(undefined);
  };

  const dropDownRender = (menu: JSX.Element): JSX.Element => {
    if ((isLoading || isFetching) && !offset)
      return (
        <div style={{ textAlign: 'center', padding: 10 }}>
          <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} spinning />
        </div>
      );

    const moreResultsJSX =
      (isLoading || isFetching) && offset ? (
        <div style={{ padding: '5px 10px' }}>
          <Space>
            <Spin size="small" indicator={<BlockLoader direction="loader loader--slideUp" />} />
          </Space>
        </div>
      ) : null;

    return (
      <div>
        {menu}
        {moreResultsJSX}
      </div>
    );
  };

  /* ****************** Render ****************** */
  return (
    <>
      <Title level={5} style={styles.titleColor}>
        {CreateSalesOrderType.SALES_ORDER_TYPE}
      </Title>
      <Select
        value={salesOrderType !== null ? salesOrderType : 'SO'}
        dropdownRender={dropDownRender}
        filterOption={(input, option): boolean => (option?.label as unknown as string).toLowerCase().includes(input.toLowerCase())}
        // filterOption={false}
        loading={isLoading || isFetching}
        onClear={handleClear}
        disabled={isPosted}
        // onPopupScroll={handleScroll}
        // onSearch={handleSearch}
        onSelect={handleSelect}
        options={salesOrderTypesOptions}
        showSearch
        style={styles.formItem}
      />
    </>
  );
};
